import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Box,
  Container,
  Divider,
  Stack,
  Paper,
  Button,
} from '@mui/material';
import { SearchOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../../components/Alert';
import { isNotNullOrEmptyArray } from '../../../globals/utils';
import api from '../../../globals/axios';
import LoaderContainer from '../../../components/LoaderContainer';

const ProgramDetail = () => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const [programDetails, setProgramDetails] = useState({});
  const { programId } = useParams();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgramDetails();
  }, []);

  const fetchProgramDetails = async () => {
    try {
      const res = await api.get(`/farm-activities/signup-program/${programId}`);
      setProgramDetails(res.data.data);
    } catch (error) {
      console.log('Error fetching program details:', error);
      showAlert('error', t('Failed to fetch program details'));
    } finally {
      setLoading(false);
    }
  };

  const handleActivityStatusUpdate = async (activityId, status) => {
    try {
      await api.patch('/farm-activities/update-activity-status/', {
        activity_id: activityId,
        activity_status: status,
      });
      showAlert('success', `Activity marked as ${status}`);
      fetchProgramDetails();
    } catch (error) {
      console.error('Error updating activity status:', error);
      showAlert('error', `Failed to update status`);
    }
  };

  return (
    <LoaderContainer loading={loading}>
      <Container maxWidth="xs">
        {!programDetails || Object.keys(programDetails).length === 0 ? (
          <Box
            sx={{
              mt: 6,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Card
              sx={{
                width: '100%',
                boxShadow: 6,
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: 6, textAlign: 'center' }}>
                {/* Icon Section */}
                <Box mb={3}>
                  <SearchOff
                    sx={{
                      fontSize: 80,
                      color: 'text.secondary',
                      opacity: 0.7,
                    }}
                  />
                </Box>

                {/* Main Message */}
                <Typography
                  variant="h4"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 2,
                  }}
                >
                  {t('Program Not Found')}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ) : (
          <Box mt={6} display="flex" justifyContent="center">
            <Card sx={{ width: '100%', boxShadow: 4, borderRadius: 3 }}>
              <CardContent>
                <Typography
                  variant="h4"
                  align="center"
                  gutterBottom
                  color="primary"
                >
                  {t('Program Details')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      {t('Program Name')}
                    </Typography>
                    <Typography variant="h6">{programDetails.name}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      {t('Start Date')}
                    </Typography>
                    <Typography variant="subtitle1">
                      {programDetails.start_date}
                    </Typography>
                  </Grid>
                </Grid>
                {/* Stages and Activities */}
                {isNotNullOrEmptyArray(programDetails.stages) && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h5" gutterBottom>
                      {t('Stages')}
                    </Typography>
                    <Stack spacing={2}>
                      {programDetails.stages.map(stage => (
                        <Paper key={stage.id} elevation={2} sx={{ p: 2 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {stage.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ mb: 1 }}
                          >
                            {t('Day')} {stage.start_day} - {stage.end_day}
                          </Typography>
                          {isNotNullOrEmptyArray(stage.activities) && (
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                sx={{ mb: 1 }}
                              >
                                {t('Activities')}
                              </Typography>
                              <Stack spacing={1}>
                                {stage.activities
                                  .filter(
                                    activity =>
                                      activity.activity_status === 'PENDING'
                                  )
                                  .map(activity => (
                                    <Paper
                                      key={activity.id}
                                      variant="outlined"
                                      sx={{ p: 1.5, background: '#f9f9f9' }}
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight="bold"
                                      >
                                        {activity.name}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                      >
                                        {activity.description}
                                      </Typography>
                                      <Grid
                                        container
                                        spacing={1}
                                        sx={{ mt: 1 }}
                                      >
                                        <Grid item xs={6}>
                                          <Typography
                                            variant="caption"
                                            color="textSecondary"
                                          >
                                            {t('Start')}:{' '}
                                            {activity.scheduled_start_date}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                          <Typography
                                            variant="caption"
                                            color="textSecondary"
                                          >
                                            {t('End')}:{' '}
                                            {activity.scheduled_end_date}
                                          </Typography>
                                        </Grid>
                                        <Grid item>
                                          <Chip
                                            label={activity.priority}
                                            color={
                                              activity.priority === 'CRITICAL'
                                                ? 'error'
                                                : 'warning'
                                            }
                                            size="small"
                                            sx={{ mt: 1 }}
                                          />
                                        </Grid>
                                        <Grid item>
                                          <Chip
                                            label={activity.activity_status}
                                            color={
                                              activity.activity_status ===
                                              'PENDING'
                                                ? 'warning'
                                                : 'success'
                                            }
                                            size="small"
                                            sx={{ mt: 1 }}
                                          />
                                        </Grid>
                                      </Grid>
                                      {activity.activity_status ===
                                        'PENDING' && (
                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                          <Button
                                            variant="contained"
                                            color="success"
                                            size="small"
                                            sx={{ mr: 1 }}
                                            onClick={() =>
                                              handleActivityStatusUpdate(
                                                activity.id,
                                                'COMPLETED'
                                              )
                                            }
                                          >
                                            {t('Completed')}
                                          </Button>
                                          <Button
                                            variant="outlined"
                                            color="warning"
                                            size="small"
                                            onClick={() =>
                                              handleActivityStatusUpdate(
                                                activity.id,
                                                'SKIPPED'
                                              )
                                            }
                                          >
                                            {t('Skipped')}
                                          </Button>
                                        </Grid>
                                      )}
                                    </Paper>
                                  ))}
                              </Stack>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </Container>
    </LoaderContainer>
  );
};

export default ProgramDetail;
